import { useMemo } from "react";
import { useStore } from "ra-core";

import type { DealStage, LabeledValue, NoteStatus } from "../types";
import { defaultConfiguration } from "./defaultConfiguration";

export const CONFIGURATION_STORE_KEY = "app.configuration";

export interface ConfigurationContextValue {
  companySectors: LabeledValue[];
  currency: string;
  dealCategories: LabeledValue[];
  dealPipelineStatuses: string[];
  dealStages: DealStage[];
  connectionPipelineStages: LabeledValue[];
  companyLifecycleStages: LabeledValue[];
  noteStatuses: NoteStatus[];
  taskTypes: LabeledValue[];
  title: string;
  darkModeLogo: string;
  lightModeLogo: string;
  googleWorkplaceDomain?: string;
  disableEmailPasswordAuthentication?: boolean;
}

export const useConfigurationContext = () => {
  const [config] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
    defaultConfiguration,
  );
  // Merge with defaults so that missing fields in stored config
  // fall back to default values (e.g. when new settings are added)
  return useMemo(
    () => ({
      ...defaultConfiguration,
      ...config,
      // Enforce code-defined lifecycle stages to prevent stale browser cache desync
      connectionPipelineStages: defaultConfiguration.connectionPipelineStages,
      companyLifecycleStages: defaultConfiguration.companyLifecycleStages,
      // Auth config belongs to the deployment, not to stored preferences.
      // Without this, a config cached before Google sign-in existed - or one
      // returned by `getConfiguration()` - would keep the button hidden.
      googleWorkplaceDomain: defaultConfiguration.googleWorkplaceDomain,
      disableEmailPasswordAuthentication:
        defaultConfiguration.disableEmailPasswordAuthentication,
    }),
    [config],
  );
};

export const useConfigurationUpdater = () => {
  const [, setConfig] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
  );
  return setConfig;
};
